import React, { useState, useEffect } from 'react';
import { Button, FormGroup, Modal, ModalBody, ModalFooter, ModalHeader } from 'shards-react';
import NotSavedAlert from './NotSavedAlert';
import TableReview from './TableReview';
import SelectApproverViewer from './SelectApproverViewer';
import { useTranslation } from 'react-i18next';
import { RootState, useDispatch, useSelector } from '../../../redux/store';
import { closeDialog, showErrorMessage, updateCondition } from '../../../redux/slices/condition';
import isConditionCustomizeUpdated from '../../../utils/manage-condition/isConditionCustomizeUpdated';
import moment from 'moment';
import { CONDITION_STATUS } from '../../../constants';
import type { UpdateConditionParams } from '../../../types/condition';
import type { UserType } from '../../../types/user';

const ModalRequestApproval = (): React.ReactElement => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { dialogMode, isDialogOpen, condition, conditionOrg } = useSelector((state: RootState) => state.condition);
  const { adminPowerUsers } = useSelector((state: RootState) => state.user);
  const { name } = useSelector((state: RootState) => state.session);
  const [isNotSaved, setIsNotSaved] = useState<boolean>(false);
  const [approverId, setApproverId] = useState<string>('');
  const [viewerId, setViewerId] = useState<string>('');

  // SIDE EFFECT
  // ================================================================================

  // 確認用
  // useEffect(() => {
  // }, []);

  // 選択肢をセット
  useEffect(() => {
    if (condition) {
      setApproverId(condition.approverId ?? '');
      setViewerId(condition.viewerId ?? '');
    }
  }, [condition]);

  // 詳細可視制御に変更があるかをチェックする
  useEffect(() => {
    // console.log(condition !== undefined, conditionOrg !== undefined, isConditionCustomizeUpdated(condition?.detailCustomize, conditionOrg?.detailCustomize));
    if (condition !== undefined && conditionOrg !== undefined && isConditionCustomizeUpdated(condition.detailCustomize, conditionOrg.detailCustomize)) {
      setIsNotSaved(true);
    } else {
      setIsNotSaved(false);
    }
  }, [condition?.detailCustomize]);

  // EVENT HANDLER
  // ================================================================================

  const resetModal = (): void => {
    dispatch(closeDialog());
    setIsNotSaved(false);
    setApproverId('');
    setViewerId('');
  };

  /**
   * TOを変更したとき
   */
  const handleChangeApprover = (option: UserType): void => {
    setApproverId(option.id);
  };

  /**
   * CCを変更したとき
   */
  const handleChangeViewer = (option: UserType): void => {
    setViewerId(option.id);
  };

  /**
   * モーダルでキャンセルボタンを押した時
   */
  const handleCancel = (): void => {
    resetModal();
  };

  /**
   * モーダルでOKボタンを押した時
   */
  const handleSave = (): void => {
    if (condition && approverId && viewerId) {
      const params: UpdateConditionParams = {
        // id: condition.id,
        conditionStatus: CONDITION_STATUS.REQUEST,
        approverId,
        viewerId,
        applicantId: name,
        requestDate: moment(new Date()).toISOString(),
        judgmentDate: null,
      };
      dispatch(updateCondition(condition.id, params));
    } else {
      dispatch(showErrorMessage(t('controlVisible.dialog.request.error')));
    }
    dispatch(closeDialog());
  };

  // VIEW
  // ================================================================================

  return (
    <Modal
      id="editConfirmationModal"
      className="modal-dialog-scrollable"
      open={isDialogOpen && dialogMode === 'REQUEST_APPROVAL'}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth={'lg'}
      size={'lg'}
      fullWidth={true}
      centered={true}
      toggle={handleCancel}>
      <ModalHeader>
        <div>
          {t('condition.request.request')}
          <NotSavedAlert saved={!isNotSaved} />
        </div>
      </ModalHeader>
      <ModalBody>
        <FormGroup>
          <label htmlFor="approver">{t('condition.request.approver')}</label>
          <SelectApproverViewer id="selectApprover" className="mb-3" placeholder={t('dropdown.default')} selected={approverId} options={adminPowerUsers?.admin ?? []} onChange={handleChangeApprover} />
          <label htmlFor="viewer">{t('condition.request.cc')}</label>
          <SelectApproverViewer id="selectViewer" className="mb-3" placeholder={t('dropdown.default')} selected={viewerId} options={adminPowerUsers?.poweruser ?? []} onChange={handleChangeViewer} />
        </FormGroup>
        <TableReview condition={condition} />
      </ModalBody>
      <ModalFooter>
        <Button theme="light" onClick={handleCancel}>
          {t('dialog.cancel')}
        </Button>
        <Button theme="primary" disabled={approverId === '' || viewerId === '' || isNotSaved} onClick={handleSave}>
          {t('dialog.request')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ModalRequestApproval;
